import {
  Component,
  Input,
  Output,
  EventEmitter,
  forwardRef,
  ViewChild,
  ElementRef,
  ChangeDetectorRef,
  OnInit,
  OnDestroy,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';

@Component({
  selector: 'app-search-box',
  templateUrl: './app-search-box.component.html',
  styleUrls: ['./app-search-box.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => AppSearchBoxComponent),
      multi: true,
    },
  ],
})
export class AppSearchBoxComponent
  implements ControlValueAccessor, OnInit, OnDestroy
{
  @Input() placeholder: string = 'Search...';
  @Input() width: string = '280px';
  @Input() maxWidth: string = '280px';
  @Input() height: string = '36px';
  @Input() disabled: boolean = false;
  @Input() readonly: boolean = false;
  @Input() clearable: boolean = true;
  @Input() autoFocus: boolean = false;
  @Input() debounce: number = 0;
  @Input() customClass: string = '';

  @Output() searchChange = new EventEmitter<string>();
  @Output() search = new EventEmitter<string>();
  @Output() clear = new EventEmitter<void>();
  @Output() inputFocus = new EventEmitter<FocusEvent>();
  @Output() inputBlur = new EventEmitter<FocusEvent>();
  @Output() inputKeyup = new EventEmitter<KeyboardEvent>();

  @ViewChild('searchInput') searchInput!: ElementRef<HTMLInputElement>;

  value: string = '';
  isFocused: boolean = false;

  private searchSubject = new Subject<string>();
  private searchSub?: Subscription;

  private onChange: (value: string) => void = () => {};
  private onTouched: () => void = () => {};

  constructor(private cdr: ChangeDetectorRef) {}

  ngOnInit(): void {
    if (this.debounce > 0) {
      this.searchSub = this.searchSubject
        .pipe(debounceTime(this.debounce), distinctUntilChanged())
        .subscribe((val) => {
          this.searchChange.emit(val);
          this.search.emit(val);
        });
    }
  }

  ngOnDestroy(): void {
    this.searchSub?.unsubscribe();
  }

  writeValue(val: any): void {
    this.value = val !== undefined && val !== null ? String(val) : '';
    this.cdr.markForCheck();
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    this.cdr.markForCheck();
  }

  onModelChange(val: string): void {
    this.value = val;
    this.onChange(val);

    if (this.debounce > 0) {
      this.searchSubject.next(val);
    } else {
      this.searchChange.emit(val);
      this.search.emit(val);
    }
  }

  onFocus(event: FocusEvent): void {
    this.isFocused = true;
    this.inputFocus.emit(event);
  }

  onBlur(event: FocusEvent): void {
    this.isFocused = false;
    this.onTouched();
    this.inputBlur.emit(event);
  }

  onKeyup(event: KeyboardEvent): void {
    this.inputKeyup.emit(event);
  }

  handleClear(): void {
    this.value = '';
    this.onChange('');
    this.searchChange.emit('');
    this.search.emit('');
    this.clear.emit();
    if (this.searchInput?.nativeElement) {
      this.searchInput.nativeElement.focus();
    }
  }
}
